import React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { td } from '@/lib/i18n';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, CheckCircle, History, Pause, Play, Plus, Trash2, Wallet } from 'lucide-react';
import { fetchDriverLedger } from '@/services/driver';
import { addLedgerEntry } from '@/services/driver-payout';
import { syncCampaignAssignmentPayout } from '@/services/campaign-assignment';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { TextField } from '@/components/text-field';
import { TextAreaField } from '@/components/textarea-field';
import { SelectField } from '@/components/select-field';
import { DateField } from '@/components/date-field';
import { FileDropZone } from '@/components/file-drop-zone';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, } from '@/components/ui/alert-dialog';
import { driverSchema } from '@/schema/driver-schema';
import { deleteDriver, fetchDriver, fetchDriverAssignments, patchDriverStatus, putDriver, uploadDriverImage, } from '@/services/driver';
import { isInternalAdmin } from '@/lib/roles';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { errorHandler } from '@/lib/utils';
import { format } from 'date-fns';
export const Route = createFileRoute('/console/_authenticated/_layout/internal/_layout/drivers/$publicId')({
    component: DriverDetailPage,
});
const STATUS_VARIANT = {
    active: 'default',
    paused: 'secondary',
    inactive: 'outline',
};
function DriverDetailPage() {
    const { t } = useTranslation('internal');
    const { publicId } = Route.useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuthenticationState();
    const canAdmin = isInternalAdmin(user);
    const { data: driver, isLoading } = useQuery({
        queryKey: ['driver', publicId],
        queryFn: () => fetchDriver(publicId),
    });
    if (isLoading) {
        return <p className="p-6 text-sm text-muted-foreground">{t('common.loading')}</p>;
    }
    if (!driver) {
        return <p className="p-6 text-sm text-muted-foreground">—</p>;
    }
    const onSaved = (updated) => {
        queryClient.setQueryData(['driver', publicId], updated);
        queryClient.invalidateQueries({ queryKey: ['drivers'] });
    };
    return (<div className="flex flex-col gap-6 p-6 max-w-3xl mx-auto w-full">
            <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/console/internal/drivers' })}>
                    <ArrowLeft className="h-4 w-4"/>
                    {t('drivers.page.back')}
                </Button>
                <Badge variant={STATUS_VARIANT[driver.status]}>{td(t, `enum.driver_status.${driver.status}`)}</Badge>
            </div>

            <div className="space-y-1">
                <h1 className="text-3xl font-semibold">{driver.fullName}</h1>
                <p className="text-sm text-muted-foreground">
                    {td(t, `enum.vehicle_type.${driver.vehicleType}`)} · {driver.licensePlate} · {driver.phoneNumber}
                </p>
            </div>

            <DriverEditCard driver={driver} onSaved={onSaved}/>

            <DriverAssignmentsCard publicId={publicId}/>

            <DriverStatusCard driver={driver} onSaved={onSaved}/>

            <DriverLedgerCard publicId={publicId}/>

            {canAdmin && <DriverDangerZone driver={driver}/>}
        </div>);
}
function DriverEditCard({ driver, onSaved, }) {
    const { t } = useTranslation('internal');
    const [photoFile, setPhotoFile] = React.useState();
    const form = useForm({
        resolver: yupResolver(driverSchema),
        defaultValues: {
            firstName: driver.firstName,
            lastName: driver.lastName,
            phoneNumber: driver.phoneNumber,
            telegramHandle: driver.telegramHandle ?? '',
            vehicleType: driver.vehicleType,
            licensePlate: driver.licensePlate,
            payoutMethod: driver.payoutMethod,
            payoutAccount: driver.payoutAccount ?? '',
            status: driver.status,
            locale: driver.locale,
            joinedAt: driver.joinedAt.slice(0, 10),
            notes: driver.notes ?? '',
        },
    });
    const { mutateAsync: saveFields, isPending: isSavePending } = useMutation({
        mutationFn: (data) => putDriver({ publicId: driver.publicId, data }),
        onError: errorHandler,
    });
    const { mutateAsync: uploadPhoto, isPending: isUploadPending } = useMutation({
        mutationFn: (file) => uploadDriverImage({ publicId: driver.publicId, file }),
        onError: errorHandler,
    });
    const isPending = isSavePending || isUploadPending;
    const handleSave = async (data) => {
        let updated = await saveFields(data);
        if (photoFile) {
            updated = await uploadPhoto(photoFile);
            setPhotoFile(undefined);
        }
        toast.success(t('drivers.toast.updated'));
        onSaved(updated);
    };
    const vehicleOptions = ['tuk_tuk', 'motorbike', 'car', 'other'].map((value) => ({
        value,
        label: td(t, `enum.vehicle_type.${value}`),
    }));
    const payoutOptions = ['cash', 'aba_pay', 'wing', 'acleda_bank', 'other'].map((value) => ({
        value,
        label: td(t, `enum.payout_method.${value}`),
    }));
    // Each language written in its own script — the staff member setting this is
    // matching it against what the driver told them, not translating it.
    const localeOptions = ['km', 'en', 'fr'].map((value) => ({
        value,
        label: td(t, `enum.driver_locale.${value}`),
    }));
    return (<Form {...form}>
            <Card>
                <CardHeader>
                    <CardTitle>{t('drivers.section.edit')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <p className="text-sm font-medium">{t('drivers.section.vehicle_photo')}</p>
                        {driver.vehicleImageUrl && !photoFile && (<img src={driver.vehicleImageUrl} alt={`${driver.fullName} vehicle`} className="max-w-sm rounded-md border" loading="lazy"/>)}
                        <FileDropZone images={photoFile ? [photoFile] : []} onDrop={(files) => setPhotoFile(files[0])} onUpload={(e) => e.target.files && setPhotoFile(e.target.files[0])} onRemove={() => setPhotoFile(undefined)}/>
                    </div>

                    <form className="grid gap-4 md:grid-cols-2" onSubmit={(e) => e.preventDefault()}>
                        <TextField control={form.control} name="firstName" label={t('drivers.field.first_name')} disabled={isPending}/>
                        <TextField control={form.control} name="lastName" label={t('drivers.field.last_name')} disabled={isPending}/>
                        <TextField control={form.control} name="phoneNumber" label={t('drivers.field.phone')} disabled={isPending}/>
                        <TextField control={form.control} name="telegramHandle" label={t('drivers.field.telegram_optional')} disabled={isPending}/>
                        <SelectField control={form.control} name="vehicleType" label={t('drivers.field.vehicle_type')} options={vehicleOptions} disabled={isPending}/>
                        <TextField control={form.control} name="licensePlate" label={t('drivers.field.license_plate')} disabled={isPending}/>
                        <SelectField control={form.control} name="payoutMethod" label={t('drivers.field.payout_method')} options={payoutOptions} disabled={isPending}/>
                        <TextField control={form.control} name="payoutAccount" label={t('drivers.field.payout_account')} disabled={isPending}/>
                        <DateField control={form.control} name="joinedAt" label={t('drivers.field.joined_on')} disabled={isPending}/>
                        <SelectField control={form.control} name="locale" label={t('drivers.field.language')} description={t('drivers.field.language_help')} options={localeOptions} disabled={isPending}/>
                    </form>

                    <TextAreaField control={form.control} name="notes" label={t('drivers.field.notes')} disabled={isPending}/>

                    <Button size="sm" onClick={form.handleSubmit(handleSave)} disabled={isPending}>
                        <CheckCircle className="h-4 w-4"/>
                        {t('drivers.action.save_changes')}
                    </Button>
                </CardContent>
            </Card>
        </Form>);
}
const ASSIGNMENT_STATUS_VARIANT = {
    active: 'default',
    scheduled: 'secondary',
    ended: 'outline',
    terminated: 'outline',
};
function DriverAssignmentsCard({ publicId }) {
    const { t } = useTranslation('internal');
    const navigate = useNavigate();
    const { data, isLoading, isError } = useQuery({
        queryKey: ['driver-assignments', publicId],
        queryFn: () => fetchDriverAssignments(publicId),
    });
    const assignments = data ?? [];
    const open = assignments.filter((a) => a.status === 'active' || a.status === 'scheduled');
    const history = assignments.filter((a) => a.status === 'ended' || a.status === 'terminated');
    const showAssignButton = !isLoading && !isError && open.length === 0;
    return (<Card>
            <CardHeader>
                <CardTitle>{t('drivers.section.assignments')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="space-y-2">
                    <p className="text-sm font-medium">{t('drivers.section.assignments_current')}</p>
                    {isLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
                    {isError && <p className="text-sm text-destructive">{t('common.load_error')}</p>}
                    {showAssignButton && (<p className="text-sm text-muted-foreground">{t('drivers.section.assignments_none')}</p>)}
                    {open.map((a) => (<AssignmentRow key={a.publicId} assignment={a}/>))}
                    {showAssignButton && (<Button size="sm" variant="outline" onClick={() => navigate({
                to: '/console/internal/assignments/new',
                search: { driverPublicId: publicId },
            })}>
                            <Plus className="h-4 w-4"/>
                            {t('drivers.section.assignments_assign_button')}
                        </Button>)}
                </div>

                {history.length > 0 && (<div className="space-y-2">
                        <p className="text-sm font-medium flex items-center gap-2">
                            <History className="h-4 w-4"/>
                            {t('drivers.section.assignments_history')}
                        </p>
                        <div className="space-y-2">
                            {history.map((a) => (<AssignmentRow key={a.publicId} assignment={a}/>))}
                        </div>
                    </div>)}
            </CardContent>
        </Card>);
}
function AssignmentRow({ assignment }) {
    const { t } = useTranslation('internal');
    return (<Link to="/console/internal/assignments/$publicId" params={{ publicId: assignment.publicId }} className="block rounded-md border p-3 hover:bg-muted/50 transition-colors">
            <div className="flex items-start justify-between gap-2">
                <div className="space-y-0.5 min-w-0">
                    <p className="text-sm font-medium truncate">{assignment.campaignName}</p>
                    <p className="text-xs text-muted-foreground">
                        {assignment.startDate.slice(0, 10)} –{' '}
                        {assignment.endDate ? assignment.endDate.slice(0, 10) : t('drivers.section.assignments_open')}
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Badge variant={ASSIGNMENT_STATUS_VARIANT[assignment.status]}>
                        {td(t, `enum.assignment_status.${assignment.status}`)}
                    </Badge>
                    <ArrowRight className="h-4 w-4 text-muted-foreground"/>
                </div>
            </div>
        </Link>);
}
function DriverStatusCard({ driver, onSaved, }) {
    const { t } = useTranslation('internal');
    const queryClient = useQueryClient();
    const { mutate, isPending } = useMutation({
        mutationFn: (status) => patchDriverStatus({ publicId: driver.publicId, status }),
        onSuccess: (updated) => {
            toast.success(t('drivers.toast.status_changed', { status: td(t, `enum.driver_status.${updated.status}`) }));
            onSaved(updated);
            queryClient.invalidateQueries({ queryKey: ['drivers'] });
        },
        onError: errorHandler,
    });
    return (<Card>
            <CardHeader>
                <CardTitle>{t('drivers.section.status')}</CardTitle>
                <CardDescription>{t('drivers.section.status_desc')}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
                {driver.status !== 'active' && (<Button size="sm" variant="outline" onClick={() => mutate('active')} disabled={isPending}>
                        <Play className="h-4 w-4"/>
                        {t('drivers.action.activate')}
                    </Button>)}
                {driver.status !== 'paused' && (<Button size="sm" variant="outline" onClick={() => mutate('paused')} disabled={isPending}>
                        <Pause className="h-4 w-4"/>
                        {t('drivers.action.pause')}
                    </Button>)}
                {driver.status !== 'inactive' && (<Button size="sm" variant="secondary" onClick={() => mutate('inactive')} disabled={isPending}>
                        {t('drivers.action.deactivate')}
                    </Button>)}
            </CardContent>
        </Card>);
}
const ENTRY_KIND_TONE = {
    extra: 'text-amber-500',
    deduction: 'text-rose-500',
    advance: 'text-sky-500',
    settlement: 'text-emerald-500',
    bonus: 'text-emerald-500',
};
const PERIOD_STATE_VARIANT = {
    pending: 'outline',
    due: 'secondary',
    overdue: 'destructive',
    partially_paid: 'secondary',
    paid: 'default',
    on_hold: 'outline',
    cancelled: 'outline',
};
function DriverLedgerCard({ publicId }) {
    const { t } = useTranslation('internal');
    const [addOpen, setAddOpen] = React.useState(false);
    const { data: ledger, isLoading, isError, } = useQuery({
        queryKey: ['driver-ledger', publicId],
        queryFn: () => fetchDriverLedger(publicId),
    });
    // Pickable periods = anything not yet paid/cancelled (matches backend's
    // "closed period rejects entries" rule). The dialog defaults to the most
    // recent open period since the common case from this surface is recording
    // an installation-day advance against the current period.
    const openPeriods = (ledger?.periods ?? []).filter((p) => p.state !== 'paid' && p.state !== 'cancelled');
    return (<Card>
            <CardHeader>
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <CardTitle>{td(t, 'drivers.ledger.title')}</CardTitle>
                        <CardDescription>{td(t, 'drivers.ledger.desc')}</CardDescription>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setAddOpen(true)} disabled={!ledger}>
                        <Wallet className="h-4 w-4"/>
                        {td(t, 'drivers.ledger.add_entry')}
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-6">
                {isLoading && <p className="text-sm text-muted-foreground">{td(t, 'drivers.ledger.loading')}</p>}
                {isError && <p className="text-sm text-destructive">{td(t, 'drivers.ledger.load_error')}</p>}
                {ledger && <DriverLedgerBody ledger={ledger}/>}
            </CardContent>

            {ledger && (<AddDriverLedgerEntryDialog driverPublicId={publicId} currency={ledger.currency} periods={openPeriods} open={addOpen} onOpenChange={setAddOpen}/>)}
        </Card>);
}
const ENTRY_KIND_OPTIONS = [
    { value: 'advance', cash: true },
    { value: 'settlement', cash: true },
    { value: 'bonus', cash: true },
    { value: 'extra', cash: false },
    { value: 'deduction', cash: false },
];
function AddDriverLedgerEntryDialog({ driverPublicId, currency, periods, open, onOpenChange, }) {
    const { t } = useTranslation('internal');
    const queryClient = useQueryClient();
    const defaultPeriodPublicId = periods[0]?.payoutPublicId ?? '';
    const form = useForm({
        defaultValues: {
            payoutPublicId: defaultPeriodPublicId,
            kind: 'advance',
            occurredAt: format(new Date(), 'yyyy-MM-dd'),
            amount: '',
            method: 'cash',
            reference: '',
            notes: '',
        },
    });
    React.useEffect(() => {
        if (open) {
            form.reset({
                payoutPublicId: periods[0]?.payoutPublicId ?? '',
                kind: 'advance',
                amount: '',
                method: 'cash',
                reference: '',
                notes: '',
            });
        }
    }, [open, periods, form]);
    const kind = useWatch({ name: 'kind', control: form.control });
    const amount = useWatch({ name: 'amount', control: form.control });
    const payoutPublicId = useWatch({ name: 'payoutPublicId', control: form.control });
    const isCash = ENTRY_KIND_OPTIONS.find((o) => o.value === kind)?.cash ?? false;
    const { mutate, isPending } = useMutation({
        mutationFn: (data) => addLedgerEntry({
            publicId: data.payoutPublicId,
            data: {
                kind: data.kind,
                amount: data.amount,
                method: isCash ? data.method : undefined,
                reference: isCash ? data.reference : undefined,
                notes: data.notes,
                occurredAt: data.occurredAt,
            },
        }),
        onSuccess: () => {
            toast.success(td(t, 'drivers.ledger.toast_recorded'));
            // Refresh both this driver's ledger and any payout-detail caches
            // that reference the same period.
            queryClient.invalidateQueries({ queryKey: ['driver-ledger', driverPublicId] });
            queryClient.invalidateQueries({ queryKey: ['driver-payout'] });
            queryClient.invalidateQueries({ queryKey: ['driver-payouts'] });
            onOpenChange(false);
        },
        onError: errorHandler,
    });
    const periodOptions = periods.map((p) => ({
        value: p.payoutPublicId,
        label: `${p.periodStart.slice(0, 10)} → ${p.periodEnd.slice(0, 10)}${p.campaignName ? ` · ${p.campaignName}` : ''} · remaining ${currency} ${p.remaining}`,
    }));
    const kindOptions = ENTRY_KIND_OPTIONS.map((o) => ({
        value: o.value,
        label: td(t, `payouts.kind_option.${o.value}`),
    }));
    const methodOptions = ['cash', 'aba_pay', 'wing', 'acleda_bank', 'other'].map((value) => ({
        value,
        label: td(t, `enum.payout_method.${value}`),
    }));
    return (<Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{td(t, 'drivers.ledger.dialog_title')}</DialogTitle>
                    <DialogDescription>{td(t, 'drivers.ledger.dialog_desc')}</DialogDescription>
                </DialogHeader>

                {periods.length === 0 && <NoPeriodsCreatePanel driverPublicId={driverPublicId} disabled={isPending}/>}

                {periods.length > 0 && (<Form {...form}>
                        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                            <SelectField control={form.control} name="payoutPublicId" label={td(t, 'drivers.ledger.field_period')} options={periodOptions} disabled={isPending || periods.length <= 1}/>
                            <SelectField control={form.control} name="kind" label={td(t, 'drivers.ledger.field_kind')} options={kindOptions} disabled={isPending}/>
                            <DateField control={form.control} name="occurredAt" label={td(t, 'drivers.ledger.field_occurred_at')} disabled={isPending}/>
                            <TextField control={form.control} name="amount" label={td(t, 'drivers.ledger.field_amount', { currency })} placeholder={td(t, 'drivers.ledger.amount_placeholder')} disabled={isPending}/>
                            {isCash && (<SelectField control={form.control} name="method" label={td(t, 'drivers.ledger.field_payment_method')} options={methodOptions} disabled={isPending}/>)}
                            {isCash && (<TextField control={form.control} name="reference" label={td(t, 'drivers.ledger.field_reference')} disabled={isPending}/>)}
                            <TextAreaField control={form.control} name="notes" label={td(t, 'drivers.ledger.field_notes')} placeholder={td(t, 'drivers.ledger.notes_placeholder')} disabled={isPending}/>
                        </form>
                    </Form>)}

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                        {td(t, 'drivers.ledger.cancel')}
                    </Button>
                    {periods.length > 0 && (<Button onClick={form.handleSubmit((data) => mutate(data))} disabled={isPending || !payoutPublicId || !amount}>
                            {td(t, 'drivers.ledger.submit')}
                        </Button>)}
                </DialogFooter>
            </DialogContent>
        </Dialog>);
}
function NoPeriodsCreatePanel({ driverPublicId, disabled, }) {
    const { t } = useTranslation('internal');
    const queryClient = useQueryClient();
    const { data: assignments = [], isLoading } = useQuery({
        queryKey: ['driver-assignments', driverPublicId],
        queryFn: () => fetchDriverAssignments(driverPublicId),
    });
    // Sync only fires for assignments that already have an open period after
    // backend processes the request — it's also a no-op safe call for ones
    // that don't. Filter to currently-open assignments to avoid presenting
    // stale rows.
    const open = assignments.filter((a) => a.status === 'active' || a.status === 'scheduled');
    const { mutate, isPending } = useMutation({
        mutationFn: (assignmentPublicId) => syncCampaignAssignmentPayout(assignmentPublicId),
        onSuccess: () => {
            toast.success(td(t, 'drivers.ledger.toast_period_created'));
            // Invalidate so the dialog re-renders with the new period and
            // the regular form replaces this CTA panel.
            queryClient.invalidateQueries({ queryKey: ['driver-ledger', driverPublicId] });
        },
        onError: errorHandler,
    });
    if (isLoading) {
        return (<p className="text-sm text-muted-foreground">{td(t, 'drivers.ledger.no_periods_assignments_loading')}</p>);
    }
    if (open.length === 0) {
        return (<div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                {td(t, 'drivers.ledger.no_periods_no_assignments')}
            </div>);
    }
    return (<div className="rounded-md border border-dashed p-4 space-y-3">
            <div className="text-sm">{td(t, 'drivers.ledger.no_periods_pick_assignment')}</div>
            <div className="space-y-2">
                {open.map((a) => (<div key={a.publicId} className="flex items-center justify-between gap-3 rounded-md border p-3">
                        <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{a.campaignName}</p>
                            <p className="text-xs text-muted-foreground">
                                {td(t, 'drivers.ledger.no_periods_started_at', {
                date: a.startDate.slice(0, 10),
                currency: a.currency,
                rate: a.monthlyRate,
            })}
                            </p>
                        </div>
                        <Button size="sm" onClick={() => mutate(a.publicId)} disabled={disabled || isPending}>
                            <Plus className="h-4 w-4"/>
                            {td(t, 'drivers.ledger.create_period')}
                        </Button>
                    </div>))}
            </div>
        </div>);
}
function DriverLedgerBody({ ledger }) {
    const { t } = useTranslation('internal');
    return (<>
            <div className="grid gap-3 md:grid-cols-3 text-sm">
                <Stat label={td(t, 'drivers.ledger.stat_total_owed')} value={`${ledger.currency} ${ledger.totalOwedAllPeriods}`}/>
                <Stat label={td(t, 'drivers.ledger.stat_total_paid')} value={`${ledger.currency} ${ledger.totalPaidAllPeriods}`}/>
                <Stat label={td(t, 'drivers.ledger.stat_outstanding')} value={`${ledger.currency} ${ledger.totalRemainingAllPeriods}`} tone={ledger.totalRemainingAllPeriods === '0.00' ? 'text-emerald-500' : 'text-amber-500'}/>
            </div>

            {ledger.periods.length > 0 && (<div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                            <tr>
                                <th className="px-2 py-1.5 text-left">{td(t, 'drivers.ledger.table_period')}</th>
                                <th className="px-2 py-1.5 text-left">{td(t, 'drivers.ledger.table_campaign')}</th>
                                <th className="px-2 py-1.5 text-left">{td(t, 'drivers.ledger.table_state')}</th>
                                <th className="px-2 py-1.5 text-right">{td(t, 'drivers.ledger.table_owed')}</th>
                                <th className="px-2 py-1.5 text-right">{td(t, 'drivers.ledger.table_paid')}</th>
                                <th className="px-2 py-1.5 text-right">{td(t, 'drivers.ledger.table_remaining')}</th>
                                <th className="px-2 py-1.5"/>
                            </tr>
                        </thead>
                        <tbody>
                            {ledger.periods.map((period) => (<tr key={period.payoutPublicId} className="border-t">
                                    <td className="px-2 py-1.5 text-xs whitespace-nowrap">
                                        {period.periodStart.slice(0, 10)} → {period.periodEnd.slice(0, 10)}
                                    </td>
                                    <td className="px-2 py-1.5 text-xs">{period.campaignName ?? '—'}</td>
                                    <td className="px-2 py-1.5">
                                        <Badge variant={PERIOD_STATE_VARIANT[period.state]} className="text-[10px]">
                                            {td(t, `enum.payout_state.${period.state}`)}
                                        </Badge>
                                    </td>
                                    <td className="px-2 py-1.5 text-right tabular-nums">{period.totalOwed}</td>
                                    <td className="px-2 py-1.5 text-right tabular-nums">{period.paidTotal}</td>
                                    <td className={`px-2 py-1.5 text-right tabular-nums ${period.remaining === '0.00' ? 'text-emerald-500' : 'text-amber-500'}`}>
                                        {period.remaining}
                                    </td>
                                    <td className="px-2 py-1.5 text-right">
                                        <Button asChild size="sm" variant="ghost">
                                            <Link to="/console/internal/payouts/$publicId" params={{ publicId: period.payoutPublicId }}>
                                                {t('common.open')}
                                            </Link>
                                        </Button>
                                    </td>
                                </tr>))}
                        </tbody>
                    </table>
                </div>)}

            {ledger.entries.length > 0 ? (<ul className="divide-y">
                    {ledger.entries.map((entry) => (<DriverLedgerEntryRow key={entry.publicId} entry={entry} currency={entry.currency || ledger.currency}/>))}
                </ul>) : (<p className="text-sm text-muted-foreground">{td(t, 'drivers.ledger.no_activity')}</p>)}
        </>);
}
function DriverLedgerEntryRow({ entry, currency }) {
    const { t } = useTranslation('internal');
    return (<li className="grid grid-cols-[auto_1fr_auto] items-start gap-3 py-3 text-sm">
            <Badge variant="outline" className={ENTRY_KIND_TONE[entry.kind]}>
                {td(t, `payouts.kind_option.${entry.kind}`)}
            </Badge>
            <div className="space-y-0.5 min-w-0">
                <p className="text-xs text-muted-foreground">
                    {entry.occurredAt.slice(0, 16).replace('T', ' ')}
                    {entry.payoutPeriodStart ? ` · period ${entry.payoutPeriodStart.slice(0, 10)}` : ''}
                </p>
                {entry.payoutPublicId && (<p className="text-xs">
                        <Link to="/console/internal/payouts/$publicId" params={{ publicId: entry.payoutPublicId }} className="text-primary hover:underline">
                            View period
                        </Link>
                    </p>)}
                {entry.notes && <p className="text-xs text-muted-foreground">{entry.notes}</p>}
            </div>
            <p className={`tabular-nums font-medium ${ENTRY_KIND_TONE[entry.kind]}`}>
                {entry.signedAmount.startsWith('-') ? '' : '+'}
                {entry.signedAmount} {currency}
            </p>
        </li>);
}
function Stat({ label, value, tone }) {
    return (<div className="rounded-md border bg-muted/30 px-3 py-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className={`tabular-nums font-medium text-base ${tone ?? ''}`}>{value}</p>
        </div>);
}
function DriverDangerZone({ driver }) {
    const { t } = useTranslation('internal');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { mutate, isPending } = useMutation({
        mutationFn: () => deleteDriver(driver.publicId),
        onSuccess: () => {
            toast.success(t('drivers.toast.deleted'));
            queryClient.invalidateQueries({ queryKey: ['drivers'] });
            navigate({ to: '/console/internal/drivers' });
        },
        onError: errorHandler,
    });
    return (<Card className="border-destructive/40">
            <CardHeader>
                <CardTitle className="text-destructive">{t('drivers.section.danger_zone')}</CardTitle>
                <CardDescription>{t('drivers.section.danger_desc')}</CardDescription>
            </CardHeader>
            <CardContent>
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm" disabled={isPending}>
                            <Trash2 className="h-4 w-4"/>
                            {t('drivers.action.delete')}
                        </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>
                                {t('drivers.delete_confirm.title', { name: driver.fullName })}
                            </AlertDialogTitle>
                            <AlertDialogDescription>{t('drivers.delete_confirm.desc')}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => mutate()}>{t('common.delete')}</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </CardContent>
        </Card>);
}
